// 明細セクションのヘッダ項目(セクション見出し行)の値保持と更新をまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createSectionHeaderService(deps) {
    const {
        AGRef,
        DETAIL_HEADER_FIELD_IDS,
        DETAIL_HEADER_PANEL,
        DicVal,
        GridApi,
        GridColApi,
        GridData,
        PDT_SIZE_PATTERN_FIELD_IDS,
        SetGridData,
        columninfo,
        columns,
        entrydataRef,
        firstNonEmptyValue,
        isDetailHeaderPanel,
        isEmpty,
        isPdtSizePatternFieldId
    } = deps;

    function isDetailHeaderField(columnid) {
        return DETAIL_HEADER_FIELD_IDS.includes(String(columnid));
    }
    function filterDetailHeaderGridColumns(fields, panel) {
        if (!isDetailHeaderPanel(panel) || isEmpty(fields)) return fields;
        return fields.filter((field) => {
            if (!isDetailHeaderField(DicVal(field, "columnid", ""))) return true;
            return String(DicVal(field, "type", "")) !== String(panel.type);
        });
    }
    function filterDetailHeaderGridColDef(panel, coldef) {
        if (!isDetailHeaderPanel(panel) || isEmpty(coldef)) return coldef;
        return coldef.filter((column) => !isDetailHeaderField(DicVal(column, "field", "")));
    }
    function sectionHeaderFields(panel) {
        if (!isDetailHeaderPanel(panel) || isEmpty(columninfo)) return [];
        const source = !isEmpty(columns)
            ? columns.filter((field) => String(DicVal(field, "type", "")) === String(panel.type))
            : columninfo.filter((field) => String(DicVal(field, "type", "")) === String(panel.type));
        return DETAIL_HEADER_FIELD_IDS
            .map((columnid) => source.find((field) => field.columnid === columnid) || columninfo.find((field) => field.columnid === columnid && String(DicVal(field, "type", "")) === String(panel.type)))
            .filter((field) => !isEmpty(field));
    }
    function sectionHeaderEntryFallbackValue(columnid) {
        const candidates = isPdtSizePatternFieldId(columnid) ? PDT_SIZE_PATTERN_FIELD_IDS : [columnid];
        return firstNonEmptyValue(entrydataRef.current, candidates);
    }
    function mergeSectionHeaderEntryFallback(data) {
        const merged = isEmpty(data) ? {} : { ...data };
        DETAIL_HEADER_FIELD_IDS.forEach((columnid) => {
            const currentValue = String(DicVal(merged, columnid, "")).trim();
            if (!isEmpty(currentValue)) return;
            const fallbackValue = String(sectionHeaderEntryFallbackValue(columnid)).trim();
            if (!isEmpty(fallbackValue)) merged[columnid] = fallbackValue;
        });
        return merged;
    }
    function sectionHeaderData(panel) {
        const rows = GridData(panel);
        if (!isEmpty(rows) && rows.length !== 0) return mergeSectionHeaderEntryFallback(rows[0]);
        const api = GridApi(panel);
        if (!isEmpty(api)) {
            const data = AGRef.current.GetDatasource(api);
            if (!isEmpty(data) && data.length !== 0) return mergeSectionHeaderEntryFallback(data[0]);
        }
        return mergeSectionHeaderEntryFallback({});
    }
    function createSectionHeaderRow(panel, initialValues = {}) {
        let row = {};
        const columnApi = GridColApi(panel);
        if (!isEmpty(AGRef.current) && !isEmpty(columnApi) && typeof AGRef.current.initialrowData === "function") {
            const initialRows = AGRef.current.initialrowData(1, columnApi);
            if (Array.isArray(initialRows) && initialRows.length !== 0) {
                row = { ...initialRows[0] };
            }
        }
        sectionHeaderFields(panel)
            .filter((field) => String(DicVal(field, "hidden", "")) !== "2")
            .forEach((field) => {
                if (!(field.columnid in row)) {
                    row[field.columnid] = DicVal(field, "initvalue", "");
                }
            });
        return { ...row, ...initialValues };
    }
    function updateSectionHeaderValue(panel, columnid, value) {
        if (!isDetailHeaderPanel(panel)) return;
        if (String(columnid ?? "").trim() === "PDT_TENKAICODE") {
            if (isEmpty(entrydataRef.current) || typeof entrydataRef.current !== "object") entrydataRef.current = {};
            entrydataRef.current.PDT_TENKAICODE = String(value ?? "").trim();
        }
        const api = GridApi(panel);
        let rows = !isEmpty(api) ? AGRef.current.GetDatasource(api) : GridData(panel);
        const newrows = isEmpty(rows) || rows.length === 0
            ? [createSectionHeaderRow(panel, { [columnid]: value })]
            : rows.map((row) => ({ ...row, [columnid]: value }));
        SetGridData(panel, newrows);
        if (!isEmpty(api)) {
            const rowNodes = [];
            api.forEachNode((rowNode) => rowNodes.push(rowNode));
            if (rowNodes.length === 0) {
                api.applyTransaction({ add: newrows, addIndex: 0 });
                return;
            }
            rowNodes.forEach((rowNode, index) => {
                rowNode.setData({ ...rowNode.data, ...DicVal(newrows, index, {}) });
            });
        }
    }
    function updateSectionHeaderSizePatternValue(value) {
        if (isEmpty(value)) return;
        const targetIds = sectionHeaderFields(DETAIL_HEADER_PANEL)
            .map((field) => String(DicVal(field, "columnid", "")).trim())
            .filter((columnid) => isPdtSizePatternFieldId(columnid));
        const updateIds = targetIds.length === 0 ? ["PDT_SIZEPATUN"] : targetIds;
        updateIds.forEach((columnid) => updateSectionHeaderValue(DETAIL_HEADER_PANEL, columnid, value));
    }

    return {
        isDetailHeaderField,
        filterDetailHeaderGridColumns,
        filterDetailHeaderGridColDef,
        sectionHeaderFields,
        sectionHeaderEntryFallbackValue,
        mergeSectionHeaderEntryFallback,
        sectionHeaderData,
        createSectionHeaderRow,
        updateSectionHeaderValue,
        updateSectionHeaderSizePatternValue
    };
}
